/* Warrior UGC wall — carousel arrows and video mute.
 *
 * THE RAIL WORKS WITHOUT THIS FILE. It is a CSS scroll-snap row: it scrolls by touch, trackpad and
 * keyboard with no JS at all. This adds two things on top:
 *
 *   1. Arrows, but ONLY when the content actually overflows. Rendering them server-side would show
 *      controls for a row that does not scroll, which is worse than no controls — it invites a click
 *      that does nothing.
 *   2. A mute toggle per video, because the videos autoplay muted (the only way iOS allows autoplay)
 *      and a customer who wants the audio needs a way to get it.
 *
 * Separate file, not custom.js: this is ~3KB needed on pages that carry the section, and custom.js
 * loads on every page.
 */
(function () {
  var RM = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Auto-advance ------------------------------------------------------
     Ian: not keen on the arrows — auto-progress instead, on a controllable interval.

     The arrows are gone entirely rather than hidden. A control that exists but is never shown is dead
     code that the next person has to reason about.

     FOUR THINGS PAUSE IT, and each is a real failure mode rather than polish:

       hover / focus-within  — a customer reading a review does not want it slid away mid-sentence.
       off-screen            — advancing a carousel nobody is looking at burns CPU and, on mobile,
                               battery. It is also why an IntersectionObserver is used rather than a
                               bare setInterval.
       manual scroll         — if the customer takes control, the carousel should stop fighting them.
                               It resumes after a pause so the section does not go permanently dead.
       prefers-reduced-motion — an auto-advancing carousel is a vestibular trigger AND a WCAG 2.2.2
                               issue (moving content lasting more than 5s needs a pause mechanism).
                               Under this preference it does not start at all.
     -------------------------------------------------------------------- */
  function initRail(wrap) {
    var rail = wrap.querySelector('[data-ugc-rail]');
    if (!rail) return;

    var enabled = rail.getAttribute('data-ugc-autoplay') === 'true';
    var interval = (parseFloat(rail.getAttribute('data-ugc-interval')) || 10) * 1000;

    function overflows() {
      // 2px tolerance: sub-pixel layout means the two values are rarely exactly equal even when there
      // is nothing to scroll.
      return rail.scrollWidth - rail.clientWidth > 2;
    }

    function syncTabindex() {
      // A tab stop that cannot scroll is a nuisance, so focusability tracks the overflow state.
      if (overflows()) {
        rail.setAttribute('tabindex', '0');
      } else {
        rail.removeAttribute('tabindex');
      }
    }

    if ('ResizeObserver' in window) {
      new ResizeObserver(syncTabindex).observe(rail);
    } else {
      window.addEventListener('resize', syncTabindex);
    }
    syncTabindex();

    if (!enabled || RM) return;

    var timer = null;
    var paused = false;
    var visible = false;
    var resumeTimer = null;

    function step() {
      var card = rail.querySelector('.custom-ugc__card');
      if (!card) return rail.clientWidth * 0.8;
      var styles = window.getComputedStyle(rail);
      var gap = parseFloat(styles.columnGap || styles.gap || '16') || 16;
      return card.getBoundingClientRect().width + gap;
    }

    function advance() {
      if (paused || !visible || !overflows()) return;
      var atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
      if (atEnd) {
        // Loop back to the start rather than stopping. Stopping at the end leaves the section frozen
        // on its last card for the rest of the session.
        rail.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        rail.scrollBy({ left: step(), behavior: 'smooth' });
      }
    }

    function start() {
      if (timer) return;
      timer = window.setInterval(advance, interval);
    }

    function stop() {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    }

    // Hover and focus. `focusin`/`focusout` rather than :focus-within in CSS, because the pause has to
    // affect the timer, not just a style.
    wrap.addEventListener('pointerenter', function () { paused = true; });
    wrap.addEventListener('pointerleave', function () { paused = false; });
    wrap.addEventListener('focusin', function () { paused = true; });
    wrap.addEventListener('focusout', function () { paused = false; });

    // Manual scroll takes precedence, then hands control back after a beat.
    rail.addEventListener('pointerdown', function () {
      paused = true;
      window.clearTimeout(resumeTimer);
    });
    rail.addEventListener('pointerup', function () {
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(function () { paused = false; }, interval);
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          visible = e.isIntersecting;
          if (visible) { start(); } else { stop(); }
        });
      }, { threshold: 0.2 }).observe(wrap);
    } else {
      visible = true;
      start();
    }
  }

  /* ---- Video mute --------------------------------------------------------- */
  function initMute(btn) {
    var card = btn.closest('.custom-ugc__card');
    if (!card) return;
    var video = card.querySelector('video');
    if (!video) {
      // No video to control, so the button would be a dead control. Remove it rather than leave it.
      btn.remove();
      return;
    }

    btn.addEventListener('click', function () {
      video.muted = !video.muted;
      // `aria-pressed` tracks MUTED state: pressed means muted, which matches the icon shown.
      btn.setAttribute('aria-pressed', String(video.muted));
      btn.classList.toggle('custom-ugc__mute--on', !video.muted);
      var label = btn.querySelector('.custom-ugc__srtext');
      if (label) label.textContent = video.muted ? 'Unmute' : 'Mute';
      // Unmuting is an explicit gesture, so play() is allowed here even if autoplay was blocked.
      if (!video.muted && video.paused) video.play().catch(function () {});
    });
  }

  /* ---- Pause off-screen videos -------------------------------------------- */
  /* Several autoplaying videos in one row is real battery and bandwidth cost, and a video scrolled
     out of the rail is invisible. Pause what cannot be seen. */
  function observeVideos(rail) {
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) {
          if (v.paused && v.muted) v.play().catch(function () {});
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { root: rail, threshold: 0.25 });

    rail.querySelectorAll('video').forEach(function (v) { io.observe(v); });
  }

  /* Videos are rendered without poster/autoplay (custom-ugc-wall.liquid, perf 2026-10-17) so
     nothing downloads on page load. When the wall comes within 800px of the screen, restore the
     posters, let the clips preload, and start the visibility-driven player above. */
  function wake(wrap) {
    wrap.querySelectorAll('video[data-poster]').forEach(function (v) {
      v.poster = v.getAttribute('data-poster');
      v.removeAttribute('data-poster');
      v.preload = 'metadata';
      if (v.hasAttribute('data-autoplay')) v.autoplay = true;
    });
    var rail = wrap.querySelector('[data-ugc-rail]');
    if (rail) observeVideos(rail);
  }

  function boot() {
    document.querySelectorAll('[data-ugc-rail-wrap]').forEach(function (wrap) {
      if (wrap.getAttribute('data-ugc-ready')) return;
      wrap.setAttribute('data-ugc-ready', '1');
      initRail(wrap);
      if (!('IntersectionObserver' in window)) { wake(wrap); return; }
      var near = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) { near.disconnect(); wake(wrap); }
      }, { rootMargin: '800px 0px' });
      near.observe(wrap);
    });
    document.querySelectorAll('[data-ugc-mute]').forEach(initMute);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
